/**
 * ProfileEditor — Build and customize your digital business card
 *
 * Features:
 * - Photo upload with zoom slider
 * - Company logo upload
 * - Editable fields (name, title, email, phone, company, whatsapp, tagline, about)
 * - Social link toggles (show/hide per link)
 * - Add / remove custom links
 * - Save / Submit button with required-field validation
 * - Unsaved changes tracking (dirty state)
 *
 * API_TODO:
 * - PUT /api/users/me { ...fields } → update profile
 * - POST /api/users/me/photo (multipart) → upload photo, returns CDN URL
 * - POST /api/users/me/logo (multipart) → upload logo, returns CDN URL
 * - PUT /api/users/me/links [{ platform, url, visible }] → update links
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { Icons } from '../components/Icons';
import { isPhoneChar, validateEmail, validatePhone } from '../utils/validation';
import { apiRequest } from '../lib/api';

export default function ProfileEditor({ user, setUser, onDirtyChange, onSaveSuccess }) {
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [newLink, setNewLink] = useState({ platform: '', url: '' });
  const [showAddLink, setShowAddLink] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  const savedSnapshot = useRef(JSON.stringify(user));

  // Track dirty state and notify parent
  const markDirty = useCallback(() => {
    if (!isDirty) {
      setIsDirty(true);
      onDirtyChange?.(true);
    }
  }, [isDirty, onDirtyChange]);

  const updateField = (field, value) => {
    setUser((prev) => ({ ...prev, [field]: value }));
    markDirty();
  };

  // Validate required fields
  const validate = () => {
    const e = {};
    if (!user.name?.trim()) e.name = 'Name is required';
    if (!user.email?.trim() || !validateEmail(user.email)) e.email = 'A valid email is required';
    if (!user.phone?.trim() || !validatePhone(user.phone)) e.phone = 'A valid phone number is required';
    if (!user.photo) e.photo = 'Profile photo is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Save handler
  const handleSave = async () => {
    if (!validate()) {
      // Scroll to top so user sees errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSaving(true);
    try {
      const data = await apiRequest('/users/me', {
        method: 'PUT',
        body: user
      });
      setUser(data);
      savedSnapshot.current = JSON.stringify(data);
      setIsDirty(false);
      onDirtyChange?.(false);
      const isFirstSave = !savedOnce;
      setSavedOnce(true);
      onSaveSuccess?.(isFirstSave ? 'first' : 'update');
    } catch (error) {
      setErrors(error.payload?.fields || {});
    } finally {
      setSaving(false);
    }
  };

  const toggleLink = (id) => {
    setUser((prev) => ({
      ...prev,
      links: prev.links.map((l) => (l.id === id ? { ...l, visible: !l.visible } : l)),
    }));
    markDirty();
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('photo', file);
      const data = await apiRequest('/users/me/photo', { method: 'POST', body: formData });
      updateField('photo', data.url);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('logo', file);
      const data = await apiRequest('/users/me/logo', { method: 'POST', body: formData });
      updateField('logo', data.url);
    }
  };

  const addLink = () => {
    if (newLink.platform && newLink.url) {
      setUser((prev) => ({
        ...prev,
        links: [...prev.links, { id: `l${Date.now()}`, ...newLink, visible: true, icon: 'web' }],
      }));
      setNewLink({ platform: '', url: '' });
      setShowAddLink(false);
      markDirty();
    }
  };

  const removeLink = (id) => {
    setUser((prev) => ({
      ...prev,
      links: prev.links.filter((l) => l.id !== id),
    }));
    markDirty();
  };

  return (
    <>
      <div className="page-header">
        <div className="mono" style={{ marginBottom: 8 }}>Profile Editor</div>
        <h1 className="page-title">
          Build your <em>card</em>.
        </h1>
        <p className="page-subtitle">Fill in your details. Only name, phone, and email are required.</p>
      </div>

      <div className="page-body">
        {/* Photo & Logo */}
        <div className="panel animate-fadeUp">
          <div className="panel-label">Photo & Logo</div>
          <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>
            <div>
              <div className={`photo-upload ${errors.photo ? 'photo-upload-error' : ''}`} onClick={() => fileRef.current?.click()}>
                {user.photo ? (
                  <img src={user.photo} alt="Profile" style={{ transform: `scale(${zoom})` }} />
                ) : (
                  <div className="upload-hint">Tap to upload photo <span className="required">*</span></div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
              {errors.photo && <div className="form-error" style={{ marginTop: 6 }}>{errors.photo}</div>}
              {user.photo && (
                <div className="photo-controls">
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>Zoom</span>
                  <input
                    type="range"
                    className="photo-slider"
                    min="1"
                    max="2"
                    step="0.05"
                    value={zoom}
                    onChange={(e) => setZoom(e.target.value)}
                  />
                  <button className="btn btn-ghost-app btn-sm" onClick={() => { updateField('photo', null); setZoom(1); }}>
                    Remove
                  </button>
                </div>
              )}
            </div>
            <div>
              <div className="form-label" style={{ marginBottom: 8 }}>Company Logo</div>
              <div className="logo-upload" onClick={() => logoRef.current?.click()}>
                {user.logo ? (
                  <img src={user.logo} alt="Logo" />
                ) : (
                  <span style={{ fontSize: 10, color: 'var(--muted)', textAlign: 'center' }}>Logo</span>
                )}
              </div>
              <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
              {user.logo && (
                <button className="btn btn-ghost-app btn-sm" style={{ marginTop: 4 }} onClick={() => updateField('logo', null)}>
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="panel animate-fadeUp" style={{ animationDelay: '0.1s' }}>
          <div className="panel-label">Basic Information</div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Full Name <span className="required">*</span></label>
              <input className={`form-input ${errors.name ? 'form-input-error' : ''}`} value={user.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Your name" />
              {errors.name && <div className="form-error">{errors.name}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Title / Role</label>
              <input className="form-input" value={user.title} onChange={(e) => updateField('title', e.target.value)} placeholder="e.g., Co-Founder & CEO" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Email <span className="required">*</span></label>
              <input className={`form-input ${errors.email ? 'form-input-error' : ''}`} type="email" value={user.email} onChange={(e) => updateField('email', e.target.value)} />
              {errors.email && <div className="form-error">{errors.email}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Phone <span className="required">*</span></label>
              <input
                className={`form-input ${errors.phone ? 'form-input-error' : ''}`}
                value={user.phone}
                onChange={(e) => {
                  const v = e.target.value;
                  if (isPhoneChar(v)) updateField('phone', v);
                }}
              />
              {errors.phone && <div className="form-error">{errors.phone}</div>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Company</label>
              <input className="form-input" value={user.company} onChange={(e) => updateField('company', e.target.value)} placeholder="Company name" />
            </div>
            <div className="form-group">
              <label className="form-label">WhatsApp Number</label>
              <input
                className="form-input"
                value={user.whatsapp || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (isPhoneChar(v)) updateField('whatsapp', v);
                }}
                placeholder="+91 77981 23870"
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Company Tagline</label>
            <input className="form-input" value={user.tagline || ''} onChange={(e) => updateField('tagline', e.target.value)} placeholder="What does your company do?" />
          </div>
          <div className="form-group">
            <label className="form-label">About You</label>
            <textarea
              className="form-input form-textarea"
              value={user.about || ''}
              onChange={(e) => updateField('about', e.target.value)}
              placeholder="A short bio — this appears on your scan page"
            />
          </div>
        </div>

        {/* Social Links */}
        <div className="panel animate-fadeUp" style={{ animationDelay: '0.2s' }}>
          <div className="panel-header">
            <div>
              <div className="panel-label">Social Links</div>
              <div className="panel-title">Toggle what you share</div>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setShowAddLink(true)}>
              {Icons.plus} Add link
            </button>
          </div>

          {user.links.map((link) => (
            <div className="toggle-row" key={link.id}>
              <div className="toggle-info">
                <span className="toggle-platform">{link.platform}</span>
                <span className="toggle-url">{link.url}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  style={{ padding: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)' }}
                  onClick={() => removeLink(link.id)}
                >
                  {Icons.close}
                </button>
                <button className={`toggle-switch ${link.visible ? 'on' : ''}`} onClick={() => toggleLink(link.id)}>
                  <div className="toggle-knob" />
                </button>
              </div>
            </div>
          ))}

          {showAddLink && (
            <div style={{ marginTop: 16, padding: 16, background: 'var(--paper)', border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)' }}>
              <div className="form-row" style={{ marginBottom: 12 }}>
                <input className="form-input" placeholder="Platform name" value={newLink.platform} onChange={(e) => setNewLink((p) => ({ ...p, platform: e.target.value }))} />
                <input className="form-input" placeholder="https://..." value={newLink.url} onChange={(e) => setNewLink((p) => ({ ...p, url: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary-app btn-sm" onClick={addLink}>Add</button>
                <button className="btn btn-ghost-app btn-sm" onClick={() => setShowAddLink(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* ── Save / Submit ── */}
        <div className="save-bar animate-fadeUp" style={{ animationDelay: '0.3s' }}>
          {Object.keys(errors).length > 0 && (
            <div className="save-errors">
              {Icons.warning}
              <span>Please fill in all required fields to save your card.</span>
            </div>
          )}
          <div className="save-bar-actions">
            {isDirty && (
              <span className="save-dirty-hint">You have unsaved changes</span>
            )}
            <button
              className={`btn btn-primary-app save-btn ${saving ? 'saving' : ''}`}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="save-spinner" />
                  Saving...
                </>
              ) : (
                <>
                  {Icons.save} Save & Publish
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
